import { regionMap } from '../dict.js'
import { okpdCodeFromLabel, okpdExists, isNumericOkpd } from '@/okpd2-tree'

const MAP_TYPES = {
  fz44: [1],
  fz223: [0],
  pp615: [7],
  comm: [2, 3, 5, 6, 9, 11, 13, 14, 15, 16, 17, 18, 19, 21, 22, 23, 24, 25, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106, 108, 109, 110, 111, 112, 113, 114, 115, 116, 118, 119, 121, 136, 137, 138, 139, 141, 145, 146, 147, 148, 149, 150, 151, 152, 153, 154, 155, 156, 157, 158, 159, 161, 162, 163, 164, 167, 168, 169, 170, 171, 172, 173, 174, 175, 176, 177, 178, 179, 180, 181, 182, 183, 184, 185, 186, 187, 188, 190, 191, 192, 193, 194, 195, 196, 197, 198, 199, 200, 201, 202, 203, 204, 205, 206, 208, 209, 211, 212, 213, 214, 216, 217, 218, 219, 221],
  kzgov: [128],
  kzcomm: [123, 124, 125, 126, 127],
  plan: [],
  sale: [129, 130, 131, 132, 133, 134, 165, 166],
}

export const PLACING_WAYS_ALL = [0, 1, 2, 3, 4, 5, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29]

const KIND_BASE = [0, 2]
const KIND_PLAN = 1
const KIND_SALE = 24

const ALL_REGION_CODES = Object.keys(regionMap).map((n) => Number(n))
const KZ_CODES = ALL_REGION_CODES.filter((n) => n >= 100)

export function buildTypesFromFlags(flags) {
  const acc = new Set()
  for (const key of Object.keys(flags || {})) {
    if (!flags[key]) continue
    ;(MAP_TYPES[key] || []).forEach((id) => acc.add(id))
  }
  return Array.from(acc)
}

export function buildKindFromFlags(flags) {
  const acc = new Set(KIND_BASE)
  if (flags?.plan) acc.add(KIND_PLAN)
  if (flags?.sale) acc.add(KIND_SALE)
  return Array.from(acc)
}

function toNonNegInt(v, def = 0) {
  const n = Number(v)
  if (!isFinite(n)) return def
  return Math.max(0, Math.trunc(n))
}

function moneyOrUndef(v) {
  if (v === '' || v === null || v === undefined) return undefined
  const n = Math.trunc(Number(v))
  return Number.isFinite(n) && n >= 0 ? n : undefined
}

function buildGuaranteeValue(state, limit) {
  if (state === 'any') return undefined
  if (state === 'no') return 0
  return toNonNegInt(limit, 0)
}

function addKzCountryIfAllSelected(arr) {
  const set = new Set(arr)
  const allKzSelected = KZ_CODES.length > 0 && KZ_CODES.every((c) => set.has(c))
  if (allKzSelected && !set.has(100)) set.add(100)
  return Array.from(set)
}

/** Пустая форма ключа (как в KeyModal при создании). */
export function createBlankKeyForm() {
  return {
    name: '',
    words: [],
    docWords: [],
    regions: [],
    customers: [],
    okpd: [],
    minPrice: null,
    maxPrice: null,
    prepayment: null,
    guaranteeApp: 'any',
    guaranteeAppMax: null,
    guaranteeContract: 'any',
    guaranteeContractMax: null,
    classificatorCondition: 'or',
    flags: {
      fz44: false,
      fz223: false,
      pp615: false,
      comm: false,
      kzgov: false,
      kzcomm: false,
      plan: false,
      sale: false,
    },
  }
}

/**
 * Собирает payload для POST/PATCH /api/keys/.
 * @param {object} form — поля как в KeyModal
 * @returns {{ payload: object, error?: string }}
 */
export function buildKeyPayloadFromForm(form) {
  const name = String(form.name ?? '').trim()
  if (!name) return { error: 'Название обязательно' }

  const chosen = (form.regions || []).map((v) => Number(v)).filter((v) => !Number.isNaN(v))
  let regionCodes = chosen.length ? chosen : ALL_REGION_CODES
  regionCodes = addKzCountryIfAllSelected(regionCodes)

  const typeCodes = buildTypesFromFlags(form.flags)
  const kindCodes = buildKindFromFlags(form.flags)
  const okpdCodes = (form.okpd || [])
    .map(okpdCodeFromLabel)
    .filter((c) => okpdExists(c) && isNumericOkpd(c))

  const minP = moneyOrUndef(form.minPrice)
  const maxP = moneyOrUndef(form.maxPrice)
  const prep = moneyOrUndef(form.prepayment)

  if (minP !== undefined && maxP !== undefined && maxP < minP) {
    return { error: 'Макс. цена не может быть меньше мин. цены' }
  }

  const wordsArr = Array.isArray(form.words) ? form.words : []
  const docWordsArr = Array.isArray(form.docWords) ? form.docWords : []

  const payload = {
    name,
    words: {
      value: wordsArr.length ? wordsArr.join(',') : null,
      excluded: docWordsArr.length ? docWordsArr.join(',') : null,
    },
    docWords: { value: null },
    regions: regionCodes,
    customers: (form.customers || []).join(';'),
    classificators: okpdCodes.length ? [{ name: 'okpd2', value: okpdCodes }] : [],
    types: typeCodes,
    excludedCustomers: '',
    participants: '',
    excludedParticipants: '',
    deliveryPlaces: [],
    garDeliveryPlaces: [],
    selectedDeliveryPlaces: [],
    statuses: [],
    placingWayNames: PLACING_WAYS_ALL,
    preference: [],
    excludedPreference: [],
    kind: kindCodes,
    condition: 'or',
    classificatorCondition: form.classificatorCondition || 'or',
    regionCondition: 'or',
    inDocs: false,
    removeEnded: false,
  }

  if (minP !== undefined) payload.minPrice = minP
  if (maxP !== undefined) payload.maxPrice = maxP
  if (prep !== undefined) payload.prepayment = prep

  const gApp = buildGuaranteeValue(form.guaranteeApp, form.guaranteeAppMax)
  if (gApp !== undefined) payload.guaranteeAppMax = gApp

  const gCon = buildGuaranteeValue(form.guaranteeContract, form.guaranteeContractMax)
  if (gCon !== undefined) payload.guaranteeContractMax = gCon

  return { payload }
}
